import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const SIZES = { sm: 440, md: 560, lg: 760 };
const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

let openCount = 0;

/**
 * The one dialog shell (audit 7.5): sizes sm 440 / md 560 / lg 760, 20px radius,
 * shared overlay, Esc + click-outside to close, focus trap, focus restore.
 */
export default function Modal({
  open,
  onClose,
  title,
  size = "md",
  children,
  footer,
  closeOnOverlay = true,
  closeOnEsc = true,
  hideClose = false,
  tone, // optional: "danger" | "warning" | "success" for the header accent
  labelledBy,
}) {
  const panelRef = useRef(null);
  const lastFocus = useRef(null);
  const titleId = labelledBy || "ee-modal-title";

  useEffect(() => {
    if (!open) return undefined;
    lastFocus.current = document.activeElement;
    openCount += 1;
    document.body.classList.add("ee-modal-open");

    const node = panelRef.current;
    if (node) {
      const first = node.querySelector("[data-autofocus]") || node.querySelector(FOCUSABLE);
      (first || node).focus();
    }

    const onKey = (e) => {
      if (e.key === "Escape" && closeOnEsc) {
        e.stopPropagation();
        onClose && onClose();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) document.body.classList.remove("ee-modal-open");
      try {
        lastFocus.current && lastFocus.current.focus && lastFocus.current.focus();
      } catch (e) {}
    };
  }, [open, closeOnEsc, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="ee-modal-overlay"
      onMouseDown={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget) onClose && onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`ee-modal ee-modal--${size}${tone ? ` ee-modal--${tone}` : ""}`}
        style={{ maxWidth: SIZES[size] || SIZES.md }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {(title || !hideClose) && (
          <div className="ee-modal__head">
            {title ? (
              <h3 id={titleId} className="ee-modal__title">
                {title}
              </h3>
            ) : (
              <span />
            )}
            {!hideClose && (
              <button type="button" className="ee-modal__close" aria-label="Close" onClick={onClose}>
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        <div className="ee-modal__body">{children}</div>
        {footer ? <div className="ee-modal__foot">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}
